// =============================================================================
// dmCacheService
// =============================================================================
// PURPOSE: Pure localStorage caching functions for DM data (no React dependencies).
//
// REQUIREMENTS:
// - TTL-based expiration: conversations 5min, messages 2min, setters 10min
// - Org-scoped cache keys to prevent cross-tenant data leaks
// - Auto-cleanup when localStorage is full (removes oldest half)
//
// ARCHITECTURE:
// - Each cache entry stores: data, timestamp, orgId
// - getCache checks: TTL not expired, orgId matches
// - setCache creates org-scoped CacheEntry with current timestamp
//
// KEY DECISIONS:
// - Extracted from useInstagramDM for testability (pure functions)
// - Cache keys include filter hash to avoid stale results when filters change
// - Silent failures: cache errors should not break the UI
// =============================================================================

import { logger } from "./logger";
import { CACHE_VERSION } from "./cacheVersion";

// Cache configuration
const CACHE_PREFIX = `altara_${CACHE_VERSION}_dm_`;
export const CONVERSATIONS_CACHE_TTL = 5 * 60 * 1000; // 5 minutes
export const MESSAGES_CACHE_TTL = 2 * 60 * 1000; // 2 minutes
export const SETTERS_CACHE_TTL = 10 * 60 * 1000; // 10 minutes

interface CacheEntry<T> {
  data: T;
  timestamp: number;
  orgId: string;
}

/**
 * Get cached data if valid (not expired, same org)
 */
export const getCache = <T>(key: string, ttl: number, orgId: string): T | null => {
  try {
    const raw = localStorage.getItem(CACHE_PREFIX + key);
    if (!raw) return null;

    const entry: CacheEntry<T> = JSON.parse(raw);
    const isExpired = Date.now() - entry.timestamp > ttl;
    const isSameOrg = entry.orgId === orgId;

    if (isExpired || !isSameOrg) {
      localStorage.removeItem(CACHE_PREFIX + key);
      return null;
    }

    return entry.data;
  } catch {
    return null;
  }
};

/**
 * Store data in cache with org scope
 */
export const setCache = <T>(key: string, data: T, orgId: string): void => {
  try {
    const entry: CacheEntry<T> = { data, timestamp: Date.now(), orgId };
    localStorage.setItem(CACHE_PREFIX + key, JSON.stringify(entry));
  } catch {
    // localStorage might be full - clear old caches
    clearOldCaches();
    logger.warn("Cache write failed, cleared old entries");
  }
};

/**
 * Remove oldest half of DM caches to free up space
 * Internal use only - called when localStorage is full
 */
const clearOldCaches = (): void => {
  try {
    const keysToRemove: string[] = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key?.startsWith(CACHE_PREFIX)) {
        keysToRemove.push(key);
      }
    }
    // Remove oldest half of cached items
    keysToRemove
      .slice(0, Math.ceil(keysToRemove.length / 2))
      .forEach((k) => localStorage.removeItem(k));
  } catch {
    // Ignore cleanup errors
  }
};

/**
 * Generate cache key for conversations based on filters
 */
export const getConversationsCacheKey = (
  orgId: string,
  filters: {
    needsResponse: boolean;
    assignedTo: string | null;
    sortBy: string;
    dateStart?: string | null;
    dateEnd?: string | null;
  }
): string => {
  const filterHash = JSON.stringify({
    needsResponse: filters.needsResponse,
    assignedTo: filters.assignedTo,
    sortBy: filters.sortBy,
    dateStart: filters.dateStart ?? null,
    dateEnd: filters.dateEnd ?? null,
  });
  return `conversations_${orgId}_${btoa(filterHash).slice(0, 20)}`;
};

/**
 * Generate cache key for messages
 */
export const getMessagesCacheKey = (conversationId: string): string => {
  return `messages_${conversationId}`;
};

/**
 * Generate cache key for setters
 */
export const getSettersCacheKey = (
  orgId: string,
  dateRange?: { start: string; end: string } | null
): string => {
  if (dateRange) {
    return `setters_${orgId}_${dateRange.start}_${dateRange.end}`;
  }
  return `setters_${orgId}`;
};
